import React, { useEffect, useState, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import {
  readCurrentLog,
  getCurrentLogUri,
  listLogFiles,
} from '@/lib/fileLogger';

/**
 * Hidden in-app log viewer for diagnosing in-the-wild issues without
 * adb / Xcode. Route: /debug/logs
 */
const DebugLogs = () => {
  const [logText, setLogText] = useState('');
  const [uri, setUri] = useState('');
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const platform = Capacitor.getPlatform();
  const isNative = platform === 'ios' || platform === 'android';

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [text, currentUri, fileList] = await Promise.all([
        readCurrentLog(),
        getCurrentLogUri(),
        listLogFiles(),
      ]);
      setLogText(text);
      setUri(currentUri);
      setFiles(fileList);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(logText || '(empty)');
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {
      // Fallback: select-all in the textarea
      const ta = document.getElementById('debug-log-textarea');
      if (ta) { ta.focus(); ta.select(); }
    }
  };

  const downloadOnWeb = () => {
    const blob = new Blob([logText || ''], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `app-${new Date().toISOString().slice(0, 10)}.log`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{
      background: '#0b0b0b',
      color: '#e7e7e7',
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
      fontSize: 12,
      padding: 12,
      borderRadius: 8,
      border: '1px solid #2a2a2a',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <strong style={{ fontSize: 14 }}>Debug Logs</strong>
        <span style={{ opacity: 0.6 }}>{platform}{loading ? ' • loading…' : ''}</span>
      </div>

      <div style={{ marginBottom: 8, wordBreak: 'break-all', opacity: 0.8 }}>
        {isNative
          ? <>Path: <code>{uri || '(initializing…)'}</code></>
          : <>Web platform — file write disabled. Logs are buffered in memory only.</>}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <button onClick={refresh} style={btnStyle}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
        <button onClick={copy} style={btnStyle}>
          {copied ? 'Copied!' : 'Copy to clipboard'}
        </button>
        {!isNative && (
          <button onClick={downloadOnWeb} style={btnStyle}>Download .log</button>
        )}
      </div>

      {files.length > 0 && (
        <details style={{ marginBottom: 8, background: '#161616', padding: 8, borderRadius: 6 }}>
          <summary style={{ cursor: 'pointer' }}>Files in logs/ ({files.length})</summary>
          <ul style={{ margin: '8px 0 0 16px', padding: 0 }}>
            {files.map((f) => (
              <li key={f.name} style={{ marginBottom: 4 }}>
                <code>{f.name}</code> — {f.size ?? '?'} bytes
                {f.uri && <div style={{ opacity: 0.6, wordBreak: 'break-all' }}>{f.uri}</div>}
              </li>
            ))}
          </ul>
        </details>
      )}

      <textarea
        id="debug-log-textarea"
        readOnly
        value={logText || (loading ? '' : '(no log entries yet — interact with the app to generate some, then press Refresh)')}
        spellCheck={false}
        style={{
          width: '100%',
          height: '60vh',
          minHeight: 320,
          background: '#0e0e0e',
          color: '#d6d6d6',
          border: '1px solid #2a2a2a',
          borderRadius: 6,
          padding: 8,
          fontFamily: 'inherit',
          fontSize: 11,
          whiteSpace: 'pre',
          overflow: 'auto',
        }}
      />

      <p style={{ marginTop: 8, opacity: 0.6, fontSize: 11 }}>
        {isNative
          ? 'Tip: connect with USB and run `adb shell run-as com.greathusband.app ls files/Documents/logs/` to retrieve files. Or use "Copy to clipboard" and paste somewhere shareable.'
          : 'Open this page on the installed app (native) to see the on-device log file.'}
      </p>
    </div>
  );
};

const btnStyle = {
  background: '#1f1f1f',
  color: '#e7e7e7',
  border: '1px solid #333',
  borderRadius: 6,
  padding: '6px 10px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 12,
};

export default DebugLogs;
